import { z } from 'zod';
// ============================================================
// 디자인 토큰 (master-of-slide DesignSystem 미러)
// ============================================================
const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{3,8}$/, '색상은 #RRGGBB 형식이어야 합니다.');
export const SlideDesignSchema = z.object({
    palette: z.object({
        bg: HexColorSchema,
        text: HexColorSchema,
        accent: HexColorSchema,
    }),
    fonts: z.object({
        display: z.string().min(1),
        body: z.string().min(1),
    }),
    typeScale: z.object({
        /** hero 텍스트 픽셀 사이즈 (1920×1080 기준 권장 140~200) */
        hero: z.number().int().positive().max(400),
        /** body 텍스트 픽셀 사이즈 (권장 28~48) */
        body: z.number().int().positive().max(120),
    }),
    radius: z.number().int().min(0).max(64),
});
// ============================================================
// 페이지 — blocks 모드용 Block 디스크리미네이트 유니온
// ============================================================
export const SlideLayoutSchema = z.enum([
    'hero',
    'title-body',
    'two-column',
    'image-left',
    'image-right',
    'quote',
    'bullets',
    'image-full',
    'kpi-row',
    'blank',
]);
// 모든 block 필드는 optional — 서버가 응답을 만들 때 빠진 필드를 default 로 채워서 보낸다.
// (zod .default() 를 사용하지 않는 이유: input/output 타입 분기가 생기면 client 코드의
// type narrowing 이 엉킨다. 응답 자체는 모든 필드가 채워져 오므로 클라가 사용 시 안전.)
export const SlideBlockSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('heading'),
        level: z.enum(['hero', 'section', 'sub']).optional(),
        text: z.string().min(1),
        align: z.enum(['left', 'center', 'right']).optional(),
    }),
    z.object({
        type: z.literal('body'),
        text: z.string(),
        size: z.enum(['lg', 'md', 'sm']).optional(),
        align: z.enum(['left', 'center', 'right']).optional(),
    }),
    z.object({
        type: z.literal('bullets'),
        items: z.array(z.string().min(1)).min(1).max(7),
        style: z.enum(['dot', 'dash', 'numbered']).optional(),
    }),
    z.object({
        type: z.literal('quote'),
        text: z.string().min(1),
        attribution: z.string().optional(),
    }),
    z.object({
        type: z.literal('image'),
        assetId: z.string().uuid(),
        fit: z.enum(['cover', 'contain']).optional(),
        caption: z.string().optional(),
    }),
    z.object({
        type: z.literal('kpi'),
        label: z.string().min(1),
        value: z.string().min(1),
        delta: z.string().optional(),
    }),
    z.object({
        type: z.literal('spacer'),
        size: z.enum(['sm', 'md', 'lg']).optional(),
    }),
]);
export const SlidePageModeSchema = z.enum(['blocks', 'tsx']);
export const SlidePageBlocksContentSchema = z.object({
    mode: z.literal('blocks'),
    layout: SlideLayoutSchema,
    blocks: z.array(SlideBlockSchema),
});
export const SlidePageTsxContentSchema = z.object({
    mode: z.literal('tsx'),
    /** 단일 React 컴포넌트의 TSX 소스. esbuild-wasm 으로 컴파일 후 iframe sandbox 에서 렌더. */
    tsxSource: z.string().min(1).max(50_000),
});
export const SlidePageContentSchema = z.discriminatedUnion('mode', [
    SlidePageBlocksContentSchema,
    SlidePageTsxContentSchema,
]);
export const SlidePageSchema = z.object({
    id: z.string().uuid(),
    slideId: z.string().uuid(),
    orderIndex: z.number().int().nonnegative(),
    content: SlidePageContentSchema,
    notes: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ============================================================
// 슬라이드 (덱)
// ============================================================
export const SlideSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    isFavorite: z.boolean(),
    pageCount: z.number().int().nonnegative(),
    /** 첫 페이지의 가장 큰 텍스트 1줄 (목록 카드의 부제). 없으면 null. */
    preview: z.string().nullable(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SlideListResponseSchema = z.object({
    slides: z.array(SlideSummarySchema),
});
export const SlideSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    isFavorite: z.boolean(),
    chatSessionId: z.string().uuid().nullable(),
    design: SlideDesignSchema,
    meta: z.record(z.unknown()),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    pages: z.array(SlidePageSchema),
});
// ============================================================
// 요청 DTO
// ============================================================
export const CreateSlideRequestSchema = z.object({
    /** 미지정 시 "제목 없는 슬라이드". */
    title: z.string().min(1).max(120).optional(),
    /** 미지정 시 slides_config.defaultDesign. */
    design: SlideDesignSchema.optional(),
});
export const RenameSlideRequestSchema = z.object({
    title: z.string().min(1).max(120),
});
export const FavoriteSlideRequestSchema = z.object({
    isFavorite: z.boolean(),
});
export const SetSlideDesignRequestSchema = z.object({
    design: SlideDesignSchema,
});
const CreatePageBaseSchema = z.object({
    /** 이 페이지 ID 다음 위치에 삽입. 미지정 시 마지막. */
    afterPageId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
});
export const CreateSlidePageRequestSchema = z.union([
    CreatePageBaseSchema.extend({ content: SlidePageBlocksContentSchema }),
    CreatePageBaseSchema.extend({ content: SlidePageTsxContentSchema }),
]);
export const UpdateSlidePageRequestSchema = z.object({
    /** mode 변경 (blocks↔tsx) 도 content 통째로 교체로 표현. */
    content: SlidePageContentSchema.optional(),
    notes: z.string().max(2000).nullable().optional(),
});
export const ReorderSlidePagesRequestSchema = z.object({
    orderedIds: z.array(z.string().uuid()).min(1),
});
// ============================================================
// 자산 (이미지 등)
// ============================================================
export const SlideAssetSchema = z.object({
    id: z.string().uuid(),
    slideId: z.string().uuid(),
    filename: z.string(),
    mime: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    /** 다운로드 URL — 서버가 발급. signed 또는 라우터 endpoint. */
    url: z.string(),
    createdAt: z.string(),
});
export const SlideAssetListResponseSchema = z.object({
    assets: z.array(SlideAssetSchema),
});
// ============================================================
// 페이지 버전 히스토리
// ============================================================
export const SlidePageVersionSourceSchema = z.enum(['manual', 'tool_call', 'rollback']);
/** 메타만 — list 응답에 layout/blocks/tsxSource 본문은 포함 안 함 (페이지 수×버전 폭증 방지). */
export const SlidePageVersionSchema = z.object({
    id: z.string().uuid(),
    pageId: z.string().uuid(),
    version: z.number().int().positive(),
    mode: SlidePageModeSchema,
    source: SlidePageVersionSourceSchema,
    notes: z.string().nullable(),
    createdAt: z.string(),
    createdBy: z.string().uuid().nullable(),
    createdByName: z.string().nullable(),
    /** 표시용 1줄 요약. blocks 모드는 첫 heading text 60자, tsx 모드는 "TSX (N자)". */
    preview: z.string(),
});
export const SlidePageVersionListResponseSchema = z.object({
    versions: z.array(SlidePageVersionSchema),
});
// ============================================================
// 운영 설정 (admin /admin/slides/config)
// ============================================================
export const SlidesConfigSchema = z.object({
    authoringSystemPrompt: z.string(),
    tsxAuthoringSystemPrompt: z.string(),
    outlineSystemPrompt: z.string(),
    ingestSystemPrompt: z.string(),
    themeChoiceSystemPrompt: z.string(),
    chatInjectPrompt: z.string(),
    defaultDesign: SlideDesignSchema,
    guideFirstMessage: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string().uuid().nullable(),
});
export const UpdateSlidesConfigRequestSchema = z.object({
    authoringSystemPrompt: z.string().optional(),
    tsxAuthoringSystemPrompt: z.string().optional(),
    outlineSystemPrompt: z.string().optional(),
    ingestSystemPrompt: z.string().optional(),
    themeChoiceSystemPrompt: z.string().optional(),
    chatInjectPrompt: z.string().optional(),
    defaultDesign: SlideDesignSchema.optional(),
    guideFirstMessage: z.string().optional(),
});
//# sourceMappingURL=slides.js.map